import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Card, CloseButton, Dialog, Field, Flex, HStack, Icon, IconButton, Input, InputGroup, Portal, Stat, Table, Text, VStack } from '@chakra-ui/react';
import { EmptyState } from '@src/components/chakra-ui/empty-state';
import { NativeSelectField, NativeSelectRoot } from '@src/components/chakra-ui/native-select';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { cleanErrorMessage, formatUsd, formatWhen } from '@src/components/sco/Advisors/utils/sessionFormat';
import { CREATE_ADVISOR_CONNECT_DASHBOARD_LINK } from '@src/gql/mutations/CreateAdvisorConnectDashboardLink';
import { CREATE_ADVISOR_CONNECT_ONBOARDING_LINK } from '@src/gql/mutations/CreateAdvisorConnectOnboardingLink';
import { REFRESH_ADVISOR_CONNECT_STATUS } from '@src/gql/mutations/RefreshAdvisorConnectStatus';
import { REQUEST_ADVISOR_PAYOUT } from '@src/gql/mutations/RequestAdvisorPayout';
import { RESET_ADVISOR_STRIPE_CONNECT } from '@src/gql/mutations/ResetAdvisorStripeConnect';
import { ADVISOR_LEDGER_ENTRIES } from '@src/gql/queries/AdvisorLedgerEntries';
import { ADVISOR_PAYOUT_BALANCE } from '@src/gql/queries/AdvisorPayoutBalance';
import { ADVISOR_PAYOUTS } from '@src/gql/queries/AdvisorPayouts';
import { ScoAdvisorLedgerEntry, ScoAdvisorLedgerEntryCollection, ScoAdvisorPayout, ScoAdvisorPayoutBalance, ScoAdvisorPayoutCollection } from '@src/graphql-types';
import { ReactNode, useState } from 'react';
import { LuBanknote, LuExternalLink, LuHistory, LuRefreshCw, LuWallet } from 'react-icons/lu';
const PAGE_SIZE = 50;
const DEFAULT_PAYOUT_MIN_USD = 10;
type ConnectCountry = {
  code?: string | null;
  name?: string | null;
};
type BalanceData = {
  advisorPayoutBalance?: ScoAdvisorPayoutBalance | null;
};
type LedgerData = {
  advisorLedgerEntries?: ScoAdvisorLedgerEntryCollection | null;
};
type PayoutsData = {
  advisorPayouts?: ScoAdvisorPayoutCollection | null;
};
type OnboardingLinkData = {
  createAdvisorConnectOnboardingLink?: {
    url?: string | null;
  } | null;
};
type DashboardLinkData = {
  createAdvisorConnectDashboardLink?: {
    url?: string | null;
  } | null;
};
const connectStatusLabel = (status: string) => {
  if (status === 'enabled') {
    return 'Connected';
  }
  if (status === 'not_started') {
    return 'Not connected';
  }
  return status.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
};
const connectStatusPalette = (status: string, connectEnabled: boolean) => {
  if (!connectEnabled) {
    return 'gray';
  }
  if (status === 'enabled') {
    return 'green';
  }
  if (status === 'not_started') {
    return 'orange';
  }
  return 'yellow';
};
export const PayoutConnectCard = t0 => {
  const $ = _c(64);
  const {
    connectStatus,
    connectEnabled,
    connectReady,
    isStandard,
    hasStripeAccount: t1,
    country: t2,
    countries: t3,
    stripeConnectCountry,
    onboardingLoading,
    refreshLoading,
    viewLoading,
    onCountryChange,
    onConnect,
    onRefresh,
    onRemove,
    onView
  } = t0;
  const hasStripeAccount = t1 === undefined ? false : t1;
  const country = t2 === undefined ? "" : t2;
  let countryName;
  let t4;
  if ($[0] !== stripeConnectCountry || $[1] !== t3) {
    const countries = t3 === undefined ? [] : t3;
    countryName = countries.find(option => option.code === stripeConnectCountry)?.name || stripeConnectCountry;
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        value: "",
        label: "Select a country"
      };
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    t4 = [t5, ...countries.flatMap(_temp)];
    $[0] = stripeConnectCountry;
    $[1] = t3;
    $[2] = countryName;
    $[3] = t4;
  } else {
    countryName = $[2];
    t4 = $[3];
  }
  const countryItems = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box bg="colorPalette.subtle" borderRadius="l2" color="colorPalette.fg" flexShrink={0} padding={2}><Icon boxSize={5}><LuWallet /></Icon></Box>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text fontWeight="semibold">Stripe Connect</Text>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== connectEnabled || $[8] !== connectStatus) {
    t7 = connectStatusPalette(connectStatus, connectEnabled);
    $[7] = connectEnabled;
    $[8] = connectStatus;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== connectEnabled || $[11] !== connectStatus) {
    t8 = connectEnabled ? connectStatusLabel(connectStatus) : "Unavailable here";
    $[10] = connectEnabled;
    $[11] = connectStatus;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <HStack gap={2} wrap="wrap">{t6}<Badge colorPalette={t7} variant="subtle">{t8}</Badge></HStack>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !connectEnabled ? "Connect is disabled in this environment. Founders can still book you." : connectReady ? isStandard ? "Session payments settle on your Stripe account. Open the Stripe Dashboard to send money to your bank." : "Request a payout below once your available balance clears the holdback." : connectStatus === "not_started" ? "Founders can book you before this is set up. Connect Stripe when you want to withdraw earnings." : "Finish Stripe setup to withdraw. Founders can still book you.";
  let t11;
  if ($[16] !== t10) {
    t11 = <Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">{t10}</Text>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== countryName || $[19] !== hasStripeAccount) {
    t12 = hasStripeAccount && countryName ? <Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">Payout country: {countryName}</Text> : null;
    $[18] = countryName;
    $[19] = hasStripeAccount;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== connectEnabled || $[22] !== country || $[23] !== countryItems || $[24] !== hasStripeAccount || $[25] !== onCountryChange) {
    t13 = !hasStripeAccount ? <Field.Root maxW="28rem" mt={2}><Field.Label>Country</Field.Label><NativeSelectRoot disabled={!connectEnabled} size="sm"><NativeSelectField aria-label="Payout country" items={countryItems} value={country} onChange={event => onCountryChange?.(event.currentTarget.value)} /></NativeSelectRoot><Field.HelperText color="colorPalette.fg/60">We support payouts to the US, Canada, UK, Switzerland, and the European Economic Area.</Field.HelperText></Field.Root> : null;
    $[21] = connectEnabled;
    $[22] = country;
    $[23] = countryItems;
    $[24] = hasStripeAccount;
    $[25] = onCountryChange;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== hasStripeAccount || $[28] !== onRemove) {
    t14 = hasStripeAccount && onRemove ? <Button alignSelf="flex-start" color="colorPalette.fg/70" fontWeight="medium" height="auto" minW={0} mt={1} px={0} size="xs" variant="plain" onClick={onRemove}>Remove Stripe account</Button> : null;
    $[27] = hasStripeAccount;
    $[28] = onRemove;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t9) {
    t15 = <VStack align="stretch" flex={1} gap={1} minW="12rem">{t9}{t11}{t12}{t13}{t14}</VStack>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LuRefreshCw />;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== onRefresh || $[38] !== refreshLoading) {
    t17 = <IconButton aria-label="Refresh Connect status" loading={refreshLoading} size="sm" variant="outline" onClick={onRefresh}>{t16}</IconButton>;
    $[37] = onRefresh;
    $[38] = refreshLoading;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== connectEnabled || $[41] !== hasStripeAccount || $[42] !== onView || $[43] !== viewLoading) {
    t18 = hasStripeAccount && onView ? <Button disabled={!connectEnabled} loading={viewLoading} size="sm" variant="outline" onClick={onView}><LuExternalLink /> View account</Button> : null;
    $[40] = connectEnabled;
    $[41] = hasStripeAccount;
    $[42] = onView;
    $[43] = viewLoading;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = !connectEnabled || !hasStripeAccount && !country;
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <LuExternalLink />;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = connectReady ? "Update account" : "Connect Stripe";
  let t22;
  if ($[46] !== onConnect || $[47] !== onboardingLoading || $[48] !== t19 || $[49] !== t21) {
    t22 = <Button disabled={t19} loading={onboardingLoading} size="sm" variant="cta" onClick={onConnect}>{t20} {t21}</Button>;
    $[46] = onConnect;
    $[47] = onboardingLoading;
    $[48] = t19;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t17 || $[52] !== t18 || $[53] !== t22) {
    t23 = <HStack flexShrink={0} gap={2} justifyContent="flex-end" wrap="wrap">{t17}{t18}{t22}</HStack>;
    $[51] = t17;
    $[52] = t18;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t15 || $[56] !== t23) {
    t24 = <Card.Body><HStack align="flex-start" gap={4} wrap="wrap">{t5}{t15}{t23}</HStack></Card.Body>;
    $[55] = t15;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== connectReady || $[59] !== isStandard) {
    t25 = connectReady && isStandard ? <Card.Footer justifyContent="flex-end"><Button asChild={true} size="sm" variant="outline"><a href="https://dashboard.stripe.com/" rel="noopener noreferrer" target="_blank"><LuExternalLink /> Open Stripe Dashboard</a></Button></Card.Footer> : null;
    $[58] = connectReady;
    $[59] = isStandard;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24 || $[62] !== t25) {
    t26 = <Card.Root size="sm" variant="outline">{t24}{t25}</Card.Root>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
};
export const PayoutBalanceStats = t0 => {
  const $ = _c(30);
  const {
    availableLabel,
    availableUsd,
    pendingLabel,
    pendingUsd,
    totalUsd
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: "column",
      md: "row"
    };
    t2 = {
      md: "1px"
    };
    t3 = {
      base: "1px",
      md: "0"
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== availableLabel) {
    t4 = <Stat.Label>{availableLabel}</Stat.Label>;
    $[3] = availableLabel;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== availableUsd) {
    t5 = formatUsd(availableUsd);
    $[5] = availableUsd;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <Stat.ValueText fontSize="2xl">{t5}</Stat.ValueText>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <Stat.Root flex={1} padding={4}>{t4}{t6}</Stat.Root>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== pendingLabel) {
    t8 = <Stat.Label>{pendingLabel}</Stat.Label>;
    $[12] = pendingLabel;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== pendingUsd) {
    t9 = formatUsd(pendingUsd);
    $[14] = pendingUsd;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <Stat.ValueText fontSize="2xl">{t9}</Stat.ValueText>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <Stat.Root flex={1} padding={4}>{t8}{t10}</Stat.Root>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Stat.Label>Total credited</Stat.Label>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== totalUsd) {
    t13 = formatUsd(totalUsd);
    $[22] = totalUsd;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <Stat.Root flex={1} padding={4}>{t12}<Stat.ValueText fontSize="2xl">{t13}</Stat.ValueText></Stat.Root>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t14 || $[28] !== t7) {
    t15 = <Flex borderWidth="1px" direction={t1} divideX={t2} divideY={t3} rounded="l2">{t7}{t11}{t14}</Flex>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t7;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};
export const PayoutRequestCard = t0 => {
  const $ = _c(24);
  const {
    available,
    minPayout,
    amount,
    connectReady,
    loading,
    onAmountChange,
    onRequest
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Text fontWeight="semibold">Request payout</Text>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== minPayout) {
    t2 = formatUsd(minPayout);
    $[1] = minPayout;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <VStack align="stretch" gap={1}>{t1}<Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">Minimum {t2}. Leave the amount blank to withdraw the full available balance.</Text></VStack>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Field.Label>Amount (USD)</Field.Label>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = available > 0 ? String(available) : "";
  let t6;
  if ($[6] !== onAmountChange) {
    t6 = event => onAmountChange(event.target.value);
    $[6] = onAmountChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== amount || $[9] !== t5 || $[10] !== t6) {
    t7 = <Field.Root maxW="11rem">{t4}<InputGroup startAddon="$"><Input min={0} placeholder={t5} step="0.01" type="number" value={amount} onChange={t6} /></InputGroup></Field.Root>;
    $[8] = amount;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = !connectReady || available < minPayout;
  const t9 = connectReady && available >= minPayout ? "cta" : "outline";
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LuBanknote />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== loading || $[14] !== onRequest || $[15] !== t8 || $[16] !== t9) {
    t11 = <Button disabled={t8} loading={loading} variant={t9} onClick={onRequest}>{t10} Request payout</Button>;
    $[13] = loading;
    $[14] = onRequest;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t7) {
    t12 = <HStack align="flex-end" gap={3} wrap="wrap">{t7}{t11}</HStack>;
    $[18] = t11;
    $[19] = t7;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t3) {
    t13 = <Card.Root size="sm" variant="outline"><Card.Body gap={4}>{t3}{t12}</Card.Body></Card.Root>;
    $[21] = t12;
    $[22] = t3;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
const HistoryBlock = t0 => {
  const $ = _c(10);
  const {
    title,
    emptyTitle,
    emptyDescription,
    isEmpty,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text fontWeight="semibold">{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== emptyDescription || $[4] !== emptyTitle || $[5] !== isEmpty) {
    t2 = isEmpty ? <EmptyState description={emptyDescription} icon={<LuHistory />} size="sm" title={emptyTitle} /> : children;
    $[2] = children;
    $[3] = emptyDescription;
    $[4] = emptyTitle;
    $[5] = isEmpty;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <VStack align="stretch" gap={3}>{t1}{t2}</VStack>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};

/**
 * Supports both Connect models:
 * - **Standard**: direct charges → Open Stripe Dashboard
 * - **Express**: platform ledger + Request payout (transfer)
 */
export const PayoutsSection = () => {
  const $ = _c(146);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [country, setCountry] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionOk, setActionOk] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const balanceQuery = useQuery(ADVISOR_PAYOUT_BALANCE, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      variables: {
        pageSize: PAGE_SIZE,
        page: 1
      },
      fetchPolicy: "network-only"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ledgerQuery = useQuery(ADVISOR_LEDGER_ENTRIES, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      variables: {
        pageSize: PAGE_SIZE,
        page: 1
      },
      fetchPolicy: "network-only"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const payoutsQuery = useQuery(ADVISOR_PAYOUTS, t2);
  let t3;
  if ($[3] !== ledgerQuery.data?.advisorLedgerEntries?.items) {
    t3 = ledgerQuery.data?.advisorLedgerEntries?.items ?? [];
    $[3] = ledgerQuery.data?.advisorLedgerEntries?.items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = t3.filter(_temp2);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const firstLedgerItems = t4;
  let t5;
  if ($[7] !== payoutsQuery.data?.advisorPayouts?.items) {
    t5 = payoutsQuery.data?.advisorPayouts?.items ?? [];
    $[7] = payoutsQuery.data?.advisorPayouts?.items;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = t5.filter(_temp3);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const firstPayoutItems = t6;
  const t7 = ledgerQuery.data?.advisorLedgerEntries?.pagination?.hasNextPage;
  let t8;
  if ($[11] !== firstLedgerItems || $[12] !== ledgerQuery.fetchMore || $[13] !== t7) {
    t8 = {
      fetchMore: ledgerQuery.fetchMore,
      field: "advisorLedgerEntries",
      firstPageItems: firstLedgerItems,
      hasNextPage: t7
    };
    $[11] = firstLedgerItems;
    $[12] = ledgerQuery.fetchMore;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const {
    items: ledgerItems,
    loadMore: loadMoreLedger,
    loadingMore: loadingMoreLedger,
    hasNextPage: ledgerHasNext
  } = useAdvisorsCollectionLoadMore(t8);
  const t9 = payoutsQuery.data?.advisorPayouts?.pagination?.hasNextPage;
  let t10;
  if ($[15] !== firstPayoutItems || $[16] !== payoutsQuery.fetchMore || $[17] !== t9) {
    t10 = {
      fetchMore: payoutsQuery.fetchMore,
      field: "advisorPayouts",
      firstPageItems: firstPayoutItems,
      hasNextPage: t9
    };
    $[15] = firstPayoutItems;
    $[16] = payoutsQuery.fetchMore;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    items: payoutItems,
    loadMore: loadMorePayouts,
    loadingMore: loadingMorePayouts,
    hasNextPage: payoutsHasNext
  } = useAdvisorsCollectionLoadMore(t10);
  let t11;
  if ($[19] !== balanceQuery || $[20] !== ledgerQuery || $[21] !== payoutsQuery) {
    t11 = () => {
      balanceQuery.refetch();
      ledgerQuery.refetch();
      payoutsQuery.refetch();
    };
    $[19] = balanceQuery;
    $[20] = ledgerQuery;
    $[21] = payoutsQuery;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const refetchAll = t11;
  const [createOnboarding, onboardingState] = useMutation(CREATE_ADVISOR_CONNECT_ONBOARDING_LINK);
  const [createDashboardLink, dashboardState] = useMutation(CREATE_ADVISOR_CONNECT_DASHBOARD_LINK);
  const [refreshConnect, refreshState] = useMutation(REFRESH_ADVISOR_CONNECT_STATUS);
  const [resetConnect, resetState] = useMutation(RESET_ADVISOR_STRIPE_CONNECT);
  const [requestPayout, payoutState] = useMutation(REQUEST_ADVISOR_PAYOUT);
  const balance = balanceQuery.data?.advisorPayoutBalance;
  const hasStripeAccount = Boolean(balance?.stripeConnectAccountId);
  let t12;
  if ($[23] !== balance?.connectCountryOptions) {
    t12 = balance?.connectCountryOptions ?? [];
    $[23] = balance?.connectCountryOptions;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = t12.filter(_temp4);
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const countries = t13;
  const available = Number(balance?.availableBalanceUsd ?? 0);
  const minPayout = Number(balance?.payoutMinUsd ?? DEFAULT_PAYOUT_MIN_USD);
  const connectStatus = balance?.stripeConnectStatusKey || "not_started";
  const connectReady = connectStatus === "enabled";
  const connectEnabled = balance?.connectEnabled !== false;
  const accountType = (balance?.connectAccountType || "express") as string;
  const isStandard = accountType === "standard";
  const isExpress = !isStandard && (accountType === "express" || balance?.supportsPlatformPayout === true);
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = async (fn, fallback) => {
      setActionError(null);
      setActionOk(null);
      ;
      try {
        await fn();
      } catch (t15) {
        const err = t15;
        setActionError(cleanErrorMessage(err, fallback));
      }
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const run = t14;
  let t15;
  if ($[28] !== country || $[29] !== createOnboarding || $[30] !== hasStripeAccount) {
    t15 = () => run(async () => {
      if (!hasStripeAccount && !country) {
        setActionError("Choose the country where you will receive payouts.");
        return;
      }
      const result = await createOnboarding({
        variables: {
          input: hasStripeAccount ? {} : {
            country
          }
        }
      });
      const url = (result.data as OnboardingLinkData | null | undefined)?.createAdvisorConnectOnboardingLink?.url;
      if (url && typeof window !== "undefined") {
        window.location.href = url;
        return;
      }
      setActionError("No onboarding URL returned. Connect may be disabled on the API.");
    }, "Could not start Stripe onboarding.");
    $[28] = country;
    $[29] = createOnboarding;
    $[30] = hasStripeAccount;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleOnboarding = t15;
  let t16;
  if ($[32] !== refetchAll || $[33] !== resetConnect) {
    t16 = () => {
      setConfirmReset(false);
      return run(async () => {
        await resetConnect();
        setCountry("");
        refetchAll();
        setActionOk("Stripe account removed. Choose a country and connect again.");
      }, "Could not remove that Stripe account.");
    };
    $[32] = refetchAll;
    $[33] = resetConnect;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const handleReset = t16;
  let t17;
  if ($[35] !== createDashboardLink) {
    t17 = () => run(async () => {
      const result_0 = await createDashboardLink();
      const url_0 = (result_0.data as DashboardLinkData | null | undefined)?.createAdvisorConnectDashboardLink?.url;
      if (url_0 && typeof window !== "undefined") {
        window.location.href = url_0;
        return;
      }
      setActionError("No Stripe dashboard link returned.");
    }, "Could not open your Stripe account.");
    $[35] = createDashboardLink;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const handleViewAccount = t17;
  let t18;
  if ($[37] !== refetchAll || $[38] !== refreshConnect) {
    t18 = () => run(async () => {
      await refreshConnect();
      refetchAll();
      setActionOk("Connect status refreshed.");
    }, "Could not refresh Connect status.");
    $[37] = refetchAll;
    $[38] = refreshConnect;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const handleRefresh = t18;
  let t19;
  if ($[40] !== available || $[41] !== minPayout || $[42] !== payoutAmount || $[43] !== refetchAll || $[44] !== requestPayout) {
    t19 = () => run(async () => {
      const amount = payoutAmount.trim() ? Number(payoutAmount) : available;
      if (!amount || amount <= 0) {
        setActionError("Enter a valid payout amount.");
        return;
      }
      if (amount < minPayout) {
        setActionError(`Minimum payout is ${formatUsd(minPayout)}.`);
        return;
      }
      if (amount > available) {
        setActionError("Amount exceeds available balance.");
        return;
      }
      await requestPayout({
        variables: {
          input: {
            amountUsd: amount
          }
        }
      });
      setPayoutAmount("");
      refetchAll();
      setActionOk("Payout transfer requested.");
    }, "Could not request that payout.");
    $[40] = available;
    $[41] = minPayout;
    $[42] = payoutAmount;
    $[43] = refetchAll;
    $[44] = requestPayout;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const handlePayout = t19;
  const loading = balanceQuery.loading && !balance || ledgerQuery.loading && !ledgerItems.length && !ledgerQuery.data;
  const error = balanceQuery.error || ledgerQuery.error;
  const t20 = balance?.stripeConnectCountry;
  let t21;
  if ($[46] !== handleOnboarding) {
    t21 = () => void handleOnboarding();
    $[46] = handleOnboarding;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== handleRefresh) {
    t22 = () => void handleRefresh();
    $[48] = handleRefresh;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== hasStripeAccount) {
    t23 = hasStripeAccount ? () => setConfirmReset(true) : undefined;
    $[50] = hasStripeAccount;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== handleViewAccount || $[53] !== hasStripeAccount) {
    t24 = hasStripeAccount ? () => void handleViewAccount() : undefined;
    $[52] = handleViewAccount;
    $[53] = hasStripeAccount;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== connectEnabled || $[56] !== connectReady || $[57] !== connectStatus || $[58] !== countries || $[59] !== country || $[60] !== dashboardState.loading || $[61] !== hasStripeAccount || $[62] !== isStandard || $[63] !== onboardingState.loading || $[64] !== refreshState.loading || $[65] !== t20 || $[66] !== t21 || $[67] !== t22 || $[68] !== t23 || $[69] !== t24) {
    t25 = <PayoutConnectCard connectEnabled={connectEnabled} connectReady={connectReady} connectStatus={connectStatus} countries={countries} country={country} hasStripeAccount={hasStripeAccount} isStandard={isStandard} onboardingLoading={onboardingState.loading} refreshLoading={refreshState.loading} stripeConnectCountry={t20} viewLoading={dashboardState.loading} onConnect={t21} onCountryChange={setCountry} onRefresh={t22} onRemove={t23} onView={t24} />;
    $[55] = connectEnabled;
    $[56] = connectReady;
    $[57] = connectStatus;
    $[58] = countries;
    $[59] = country;
    $[60] = dashboardState.loading;
    $[61] = hasStripeAccount;
    $[62] = isStandard;
    $[63] = onboardingState.loading;
    $[64] = refreshState.loading;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = event => setConfirmReset(event.open);
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Dialog.Backdrop bg="blackAlpha.700" />;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <VStack align="flex-start" flex={1} gap={1} minWidth={0} paddingRight={10}><Dialog.Title fontSize="xl" fontWeight="semibold" lineHeight="short">Remove Stripe account?</Dialog.Title><Dialog.Description color="colorPalette.fg/60" fontSize="sm">Earnings already in your Startups balance stay here. You will choose a country and connect a new Stripe account.</Dialog.Description></VStack>;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Dialog.Header alignItems="flex-start" borderBottomWidth="1px" borderColor="colorPalette.border/50" paddingBottom={4} paddingTop={5} paddingX={6} position="relative">{t28}<Dialog.CloseTrigger asChild={true} insetEnd={3} position="absolute" top={3}><CloseButton size="sm" /></Dialog.CloseTrigger></Dialog.Header>;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Dialog.Body paddingX={6} paddingY={5}><Text color="colorPalette.fg/80" fontFamily="longform" textStyle="sm">Stripe cannot change the country on an account that already exists. Remove this one only when it has no balance waiting at Stripe.</Text></Dialog.Body>;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Button variant="ghost" onClick={() => setConfirmReset(false)}>Cancel</Button>;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== handleReset) {
    t32 = () => void handleReset();
    $[77] = handleReset;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== resetState.loading || $[80] !== t32) {
    t33 = <Portal>{t27}<Dialog.Positioner paddingX={4}><Dialog.Content bgColor="colorPalette.container" borderColor="colorPalette.border" borderRadius="2xl" borderWidth="1px" boxShadow="xl" maxWidth="480px" width="full">{t29}{t30}<Dialog.Footer borderColor="colorPalette.border/50" borderTopWidth="1px" gap={2} paddingX={6} paddingY={4}>{t31}<Button loading={resetState.loading} variant="cta" onClick={t32}>Remove account</Button></Dialog.Footer></Dialog.Content></Dialog.Positioner></Portal>;
    $[79] = resetState.loading;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== confirmReset || $[83] !== t33) {
    t34 = <Dialog.Root motionPreset="scale" open={confirmReset} placement="center" size="md" onOpenChange={t26}>{t33}</Dialog.Root>;
    $[82] = confirmReset;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  const t35 = isExpress ? "Available to payout" : "Earnings (log)";
  const t36 = balance?.availableBalanceUsd;
  const t37 = isExpress ? "Pending holdback" : "Pending";
  const t38 = balance?.pendingBalanceUsd;
  const t39 = balance?.totalBalanceUsd;
  let t40;
  if ($[85] !== t35 || $[86] !== t36 || $[87] !== t37 || $[88] !== t38 || $[89] !== t39) {
    t40 = <PayoutBalanceStats availableLabel={t35} availableUsd={t36} pendingLabel={t37} pendingUsd={t38} totalUsd={t39} />;
    $[85] = t35;
    $[86] = t36;
    $[87] = t37;
    $[88] = t38;
    $[89] = t39;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== available || $[92] !== connectReady || $[93] !== handlePayout || $[94] !== isExpress || $[95] !== minPayout || $[96] !== payoutAmount || $[97] !== payoutState) {
    t41 = isExpress ? <PayoutRequestCard amount={payoutAmount} available={available} connectReady={connectReady} loading={payoutState.loading} minPayout={minPayout} onAmountChange={setPayoutAmount} onRequest={() => void handlePayout()} /> : null;
    $[91] = available;
    $[92] = connectReady;
    $[93] = handlePayout;
    $[94] = isExpress;
    $[95] = minPayout;
    $[96] = payoutAmount;
    $[97] = payoutState;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== actionError || $[100] !== actionOk) {
    t42 = actionError || actionOk ? <Text color={actionError ? "red.fg" : "green.fg"} fontFamily="longform" fontSize="sm">{actionError || actionOk}</Text> : null;
    $[99] = actionError;
    $[100] = actionOk;
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  const t43 = ledgerItems.length === 0;
  let t44;
  let t45;
  let t46;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Table.ColumnHeader>When</Table.ColumnHeader>;
    t45 = <Table.ColumnHeader>Type</Table.ColumnHeader>;
    t46 = <Table.ColumnHeader>Amount</Table.ColumnHeader>;
    $[102] = t44;
    $[103] = t45;
    $[104] = t46;
  } else {
    t44 = $[102];
    t45 = $[103];
    t46 = $[104];
  }
  let t47;
  if ($[105] !== isExpress) {
    t47 = isExpress && <Table.ColumnHeader>Available</Table.ColumnHeader>;
    $[105] = isExpress;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  let t48;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Table.ColumnHeader>Note</Table.ColumnHeader>;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  let t49;
  if ($[108] !== t47) {
    t49 = <Table.Header><Table.Row>{t44}{t45}{t46}{t47}{t48}</Table.Row></Table.Header>;
    $[108] = t47;
    $[109] = t49;
  } else {
    t49 = $[109];
  }
  let t50;
  if ($[110] !== isExpress || $[111] !== ledgerItems) {
    let t51;
    if ($[113] !== isExpress) {
      t51 = row => <Table.Row key={row.advisorLedgerEntryId || row.id}><Table.Cell>{formatWhen(row.createdAt)}</Table.Cell><Table.Cell>{row.entryType?.title || row.entryTypeKey || "\u2014"}</Table.Cell><Table.Cell>{formatUsd(row.amountUsd)}</Table.Cell>{isExpress && <Table.Cell>{formatWhen(row.availableAt)}</Table.Cell>}<Table.Cell><Text color="colorPalette.fg/60" fontSize="xs" lineClamp={2}>{row.description || "\u2014"}</Text></Table.Cell></Table.Row>;
      $[113] = isExpress;
      $[114] = t51;
    } else {
      t51 = $[114];
    }
    t50 = ledgerItems.map(t51);
    $[110] = isExpress;
    $[111] = ledgerItems;
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[115] !== t50) {
    t51 = <Table.Body>{t50}</Table.Body>;
    $[115] = t50;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  let t52;
  if ($[117] !== t49 || $[118] !== t51) {
    t52 = <Box borderWidth="1px" overflowX="auto" rounded="l2"><Table.Root size="sm">{t49}{t51}</Table.Root></Box>;
    $[117] = t49;
    $[118] = t51;
    $[119] = t52;
  } else {
    t52 = $[119];
  }
  let t53;
  if ($[120] !== ledgerHasNext || $[121] !== loadMoreLedger || $[122] !== loadingMoreLedger) {
    t53 = <AdvisorsLoadMore hasNextPage={ledgerHasNext} loading={loadingMoreLedger} onLoadMore={loadMoreLedger} />;
    $[120] = ledgerHasNext;
    $[121] = loadMoreLedger;
    $[122] = loadingMoreLedger;
    $[123] = t53;
  } else {
    t53 = $[123];
  }
  let t54;
  if ($[124] !== t43 || $[125] !== t52 || $[126] !== t53) {
    t54 = <HistoryBlock emptyDescription="Completed paid sessions credit your balance here after the holdback." emptyTitle="No session earnings yet" isEmpty={t43} title="Session earnings">{t52}{t53}</HistoryBlock>;
    $[124] = t43;
    $[125] = t52;
    $[126] = t53;
    $[127] = t54;
  } else {
    t54 = $[127];
  }
  let t55;
  if ($[128] !== isExpress || $[129] !== loadMorePayouts || $[130] !== loadingMorePayouts || $[131] !== payoutItems || $[132] !== payoutsHasNext) {
    t55 = isExpress ? <HistoryBlock emptyDescription="Requested transfers to your connected account will show up here." emptyTitle="No payouts requested yet" isEmpty={payoutItems.length === 0} title="Payout history"><Box borderWidth="1px" overflowX="auto" rounded="l2"><Table.Root size="sm"><Table.Header><Table.Row><Table.ColumnHeader>Requested</Table.ColumnHeader><Table.ColumnHeader>Amount</Table.ColumnHeader><Table.ColumnHeader>Status</Table.ColumnHeader><Table.ColumnHeader>Completed</Table.ColumnHeader></Table.Row></Table.Header><Table.Body>{payoutItems.map(_temp5)}</Table.Body></Table.Root></Box><AdvisorsLoadMore hasNextPage={payoutsHasNext} loading={loadingMorePayouts} onLoadMore={loadMorePayouts} /></HistoryBlock> : null;
    $[128] = isExpress;
    $[129] = loadMorePayouts;
    $[130] = loadingMorePayouts;
    $[131] = payoutItems;
    $[132] = payoutsHasNext;
    $[133] = t55;
  } else {
    t55 = $[133];
  }
  let t56;
  if ($[134] !== t25 || $[135] !== t34 || $[136] !== t40 || $[137] !== t41 || $[138] !== t42 || $[139] !== t54 || $[140] !== t55) {
    t56 = <VStack align="stretch" gap={6}>{t25}{t34}{t40}{t41}{t42}{t54}{t55}</VStack>;
    $[134] = t25;
    $[135] = t34;
    $[136] = t40;
    $[137] = t41;
    $[138] = t42;
    $[139] = t54;
    $[140] = t55;
    $[141] = t56;
  } else {
    t56 = $[141];
  }
  let t57;
  if ($[142] !== error || $[143] !== loading || $[144] !== t56) {
    t57 = <AdvisorsQueryState error={error} loading={loading}>{t56}</AdvisorsQueryState>;
    $[142] = error;
    $[143] = loading;
    $[144] = t56;
    $[145] = t57;
  } else {
    t57 = $[145];
  }
  return t57;
};
function _temp(option_0) {
  return option_0.code && option_0.name ? [{
    value: option_0.code,
    label: option_0.name
  }] : [];
}
function _temp2(entry) {
  return !!entry;
}
function _temp3(payout) {
  return !!payout;
}
function _temp4(option) {
  return !!option;
}
function _temp5(row_0) {
  return <Table.Row key={row_0.advisorPayoutId || row_0.id}><Table.Cell>{formatWhen(row_0.requestedAt || row_0.createdAt)}</Table.Cell><Table.Cell>{formatUsd(row_0.amountUsd)}</Table.Cell><Table.Cell><Badge variant="subtle">{row_0.status?.title || row_0.statusKey || "\u2014"}</Badge></Table.Cell><Table.Cell>{formatWhen(row_0.completedAt)}</Table.Cell></Table.Row>;
}